import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: inline-block;
      position: relative;
      vertical-align: middle;
      font-size: initial;
    }
    :host([fluid])    { display: block; width: 100%; }
    /* Prevent all pointer events at the host level so events don't reach
       the host via pointer-events pass-through when the inner button is disabled. */
    :host([disabled]),
    :host([loading])  { pointer-events: none; }

    /* ── Base button ────────────────────────────────────────────────────────── */
    .uwc-btn {
      --_bg:         var(--uwc-btn-bg,        var(--uwc-color-primary,  ${t.primary}));
      --_color:      var(--uwc-btn-color,     ${t.colorWhite});
      --_border:     var(--uwc-btn-border,    transparent);
      --_radius:     var(--uwc-btn-radius,    var(--uwc-radius-md,     ${t.radiusMd}));
      --_shadow:     var(--uwc-btn-shadow,    none);
      --_font-size:  var(--uwc-btn-font-size, var(--uwc-font-size-md,  ${t.fontSizeMd}));
      /* Height comes from the shared control-height token so a button lines up
         with inputs/dropdowns at every size. Vertical padding is intentionally
         0 by default — the min-height + centering define the box; padding-y is
         NOT the height driver (that's what made buttons diverge from inputs). */
      --_height:     var(--uwc-btn-height,    ${t.controlHeightMd});
      --_px:         var(--uwc-btn-padding-x, 0.875rem);
      --_py:         var(--uwc-btn-padding-y, 0);
      --_gap:        var(--uwc-btn-gap,       0.375rem);
      --_transition: var(--uwc-btn-transition,
        background-color ${t.durationBase} ease,
        box-shadow ${t.durationBase} ease,
        opacity ${t.durationBase} ease,
        translate ${t.durationFast} ease
      );

      position: relative;
      overflow: hidden;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--_gap);
      width: 100%;
      /* Definite height pins the box to the shared control-height token so a
         button always lines up with an input at every size. white-space:nowrap
         (below) keeps the label single-line, so a fixed height never clips. Any
         themed --_py is absorbed by the box instead of inflating the height —
         that fixed padding-y is what used to make buttons taller than inputs.
         Stacked icon variants restore min-height so they can grow (see below). */
      height: var(--_height);
      padding-inline: var(--_px);
      padding-block:  var(--_py);
      font-family:    var(--uwc-font-family, inherit);
      font-size:      var(--_font-size);
      font-weight:    var(--uwc-btn-font-weight, ${t.fontWeightMedium});
      letter-spacing: var(--uwc-btn-letter-spacing, normal);
      text-transform: var(--uwc-btn-text-transform, none);
      line-height:    1.25;
      white-space:    nowrap;
      cursor:         pointer;
      user-select:    none;
      text-decoration: none;
      outline:        none;
      box-sizing:     border-box;

      border:           1px solid var(--_border);
      border-radius:    var(--_radius);
      background-color: var(--_bg);
      color:            var(--_color);
      box-shadow:       var(--_shadow);
      transition:       var(--_transition);
    }
    .uwc-btn:hover  { background-color: color-mix(in oklab, var(--_bg) 100%, white 12%); }
    .uwc-btn:active { background-color: color-mix(in oklab, var(--_bg) 100%, black 6%); translate: 0 1px; }
    .uwc-btn:focus-visible { outline: 2px solid var(--_bg); outline-offset: 2px; }

    /* ── Variants ───────────────────────────────────────────────────────────── */
    .uwc-btn--primary   { --_bg: var(--uwc-color-primary,   ${t.primary});   }
    .uwc-btn--secondary { --_bg: var(--uwc-color-secondary, ${t.secondary}); }
    .uwc-btn--success   { --_bg: var(--uwc-color-success,   ${t.success});   }
    .uwc-btn--info      { --_bg: var(--uwc-color-info,      ${t.info});      }
    .uwc-btn--warning   { --_bg: var(--uwc-color-warning,   ${t.warning}); --_color: var(--uwc-btn-warning-color, ${t.contrast}); }
    .uwc-btn--help      { --_bg: var(--uwc-color-help,      ${t.help});    }
    .uwc-btn--danger    { --_bg: var(--uwc-color-danger,    ${t.danger});  }
    .uwc-btn--contrast  { --_bg: var(--uwc-color-contrast,  ${t.contrast}); --_color: ${t.colorWhite}; }

    /* ── Sizes ──────────────────────────────────────────────────────────────────
       Component-level size attrs scale the control height from their own font so
       a small/large button stays proportional regardless of the global size. */
    .uwc-btn--small { --_font-size: var(--uwc-font-size-xs, ${t.fontSizeXs}); --_height: ${t.controlHeightSm}; --_px: 0.625rem; }
    .uwc-btn--large { --_font-size: var(--uwc-font-size-lg, ${t.fontSizeLg}); --_height: ${t.controlHeightLg}; --_px: 1.125rem; }

    /* ── Raised ─────────────────────────────────────────────────────────────── */
    .uwc-btn--raised       { --_shadow: 0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12); }
    .uwc-btn--raised:hover { --_shadow: 0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12); }

    /* ── Rounded / Outline / Text / Link ────────────────────────────────────── */
    .uwc-btn--rounded { --_radius: var(--uwc-radius-full, ${t.radiusFull}); }

    .uwc-btn--outline { --_border: var(--_bg); background-color: transparent; color: var(--_bg); }
    .uwc-btn--outline:hover  { background-color: color-mix(in oklab, var(--_bg) 12%, transparent); }
    .uwc-btn--outline:active { background-color: color-mix(in oklab, var(--_bg) 20%, transparent); }

    .uwc-btn--text { background-color: transparent; border-color: transparent; color: var(--_bg); }
    .uwc-btn--text:hover  { background-color: color-mix(in oklab, var(--_bg) 10%, transparent); }
    .uwc-btn--text:active { background-color: color-mix(in oklab, var(--_bg) 18%, transparent); }

    .uwc-btn--link { background-color: transparent; border-color: transparent; color: var(--_bg); text-decoration: underline; text-underline-offset: 2px; }
    .uwc-btn--link:hover  { background-color: transparent; color: color-mix(in oklab, var(--_bg) 80%, black); }
    .uwc-btn--link:active { translate: 0 0; }

    /* ── Icon-only / top / bottom ────────────────────────────────────────────── */
    .uwc-btn--icon-only { --_px: var(--_py); aspect-ratio: 1/1; gap: 0; justify-content: center; }
    .uwc-btn--icon-only.uwc-btn--small { --_px: 0.3125rem; }
    .uwc-btn--icon-only.uwc-btn--large { --_px: 0.6875rem; }
    /* Stacked icon+label needs real vertical room: drop the definite height and
       fall back to min-height as a floor so the column can grow past it. */
    .uwc-btn--icon-top,
    .uwc-btn--icon-bottom { flex-direction: column; gap: ${t.space1}; padding-block: ${t.space2}; height: auto; min-height: var(--_height); }
    .uwc-btn--icon-bottom { flex-direction: column-reverse; }

    /* ── States ─────────────────────────────────────────────────────────────── */
    .uwc-btn--loading  { cursor: wait; }
    .uwc-btn--disabled { pointer-events: none; opacity: 0.6; cursor: not-allowed; }
    .uwc-btn--fluid    { width: 100%; }

    /* ── Inner elements ─────────────────────────────────────────────────────── */
    .uwc-btn__label { display: inline-flex; align-items: center; }
    .uwc-btn__icon  { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }

    /* ── Badge ──────────────────────────────────────────────────────────────── */
    .uwc-btn__badge {
      --_badge-bg:    var(--uwc-btn-badge-bg,    var(--uwc-color-danger, ${t.danger}));
      --_badge-color: var(--uwc-btn-badge-color, ${t.colorWhite});
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1.125rem;
      height: 1.125rem;
      padding-inline: 0.3125rem;
      margin-inline-start: 0.125rem;
      font-size: ${t.fontSizeXs};
      font-weight: ${t.fontWeightBold};
      line-height: 1;
      border-radius: var(--uwc-radius-full, ${t.radiusFull});
      background-color: var(--_badge-bg);
      color: var(--_badge-color);
    }
    .uwc-btn__badge--primary   { --_badge-bg: var(--uwc-color-primary,   ${t.primary});   }
    .uwc-btn__badge--secondary { --_badge-bg: var(--uwc-color-secondary, ${t.secondary}); }
    .uwc-btn__badge--success   { --_badge-bg: var(--uwc-color-success,   ${t.success});   }
    .uwc-btn__badge--info      { --_badge-bg: var(--uwc-color-info,   ${t.info}); --_badge-color: #111; }
    .uwc-btn__badge--warning   { --_badge-bg: var(--uwc-color-warning, ${t.warning}); --_badge-color: #111; }
    .uwc-btn__badge--danger    { --_badge-bg: var(--uwc-color-danger,  ${t.danger});   }
    .uwc-btn__badge--help      { --_badge-bg: var(--uwc-color-help,    ${t.help});     }
    .uwc-btn__badge--contrast  { --_badge-bg: var(--uwc-color-contrast,${t.contrast}); }

    /* ── Ripple ─────────────────────────────────────────────────────────────── */
    /* CSS custom properties allow themes to tune ripple feel without JS changes.
       var() inside @keyframes is evaluated at animation-start, so it works in
       all modern browsers. */
    @keyframes uwc-ripple {
      from { opacity: var(--uwc-btn-ripple-opacity, 0.25); transform: scale(0);   }
      to   { opacity: 0; transform: scale(var(--uwc-btn-ripple-scale, 2.5)); }
    }
    .uwc-btn-ripple {
      position: absolute;
      border-radius: 50%;
      background: currentColor;
      pointer-events: none;
      animation: uwc-ripple var(--uwc-btn-ripple-duration, 500ms)
                 var(--uwc-btn-ripple-easing, ease-out) forwards;
      transform-origin: center;
    }
  `,
];
